/**
 * PdfPreviewModal — Renders a PDF Blob in an iframe inside an MUI Dialog.
 *
 * Uses the browser's native PDF viewer via URL.createObjectURL for best
 * fidelity and zero extra dependencies. Object URL is revoked on close.
 */

import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Button, Box, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useMobile } from '../../hooks/useMobile';

export default function PdfPreviewModal({ open, blob, filename, onClose }) {
  const { isMobile } = useMobile();
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!open || !blob) {
      setUrl(null);
      return undefined;
    }
    const objUrl = URL.createObjectURL(blob);
    setUrl(objUrl);
    return () => {
      URL.revokeObjectURL(objUrl);
    };
  }, [open, blob]);

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { height: isMobile ? '100%' : '92vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, pr: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {filename || 'Preview'}
        </Typography>
        {url && (isMobile ? (
          <>
            <IconButton
              size="small"
              component="a"
              href={url}
              download={filename || 'document.pdf'}
              aria-label="Download"
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              size="small"
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open in tab"
            >
              <OpenInNewIcon />
            </IconButton>
          </>
        ) : (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              component="a"
              href={url}
              download={filename || 'document.pdf'}
            >
              Download
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in tab
            </Button>
          </>
        ))}
        <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex' }}>
        {url ? (
          <iframe
            src={url}
            title={filename || 'PDF preview'}
            style={{ width: '100%', height: '100%', border: 0 }}
          />
        ) : (
          <Box sx={{ m: 'auto', color: 'text.secondary' }}>Loading…</Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
